import { apiService } from './api'
import { formatApiDetail, getApiErrorPayload } from '../utils/apiErrors'
import {
  createCaptchaRequiredError,
  parseCaptchaRequiredFromResponse,
} from '../utils/captchaErrors'
import { clearAuthToken, getAuthToken, setAuthToken } from '../utils/authStorage'

export { clearAuthToken, getAuthToken, setAuthToken }

export interface LoginRequest {
  email: string
  password: string
  captcha_token?: string
  remember_me?: boolean
}

export interface RegisterRequest {
  email: string
  password: string
  first_name?: string
  last_name?: string
  role: 'student' | 'tutor' | 'school' | 'admin' | 'affiliate'
  persona?: string
  school_name?: string
  referral_code?: string
  locale?: string
  captcha_token?: string
}

export interface AuthResponse {
  access_token?: string
  token_type: string
  requires_2fa?: boolean
  mfa_token?: string
  user_id?: string
  user_type?: string
  user?: {
    id: string
    email: string
    first_name: string
    last_name: string
    role: string
    school_name?: string
    school_id?: string
    is_active?: boolean
    photo_url?: string
    photo_filename?: string
    photo_size?: number
    photo_mime_type?: string
    photo_uploaded_at?: string
    photo?: string
    timezone?: string
    timezone_offset?: number
    last_seen?: string
  }
}

export interface UserResponse {
  id: string
  email: string
  first_name: string
  last_name: string
  role: string
  school_name?: string
  school_id?: string
  is_active?: boolean
  photo_url?: string
  photo_filename?: string
  photo_size?: number
  photo_mime_type?: string
  photo_uploaded_at?: string
  photo?: string
  timezone?: string
  timezone_offset?: number
  preferred_locale?: string | null
  voice_preference?: string | null
  last_seen?: string
  referral_attributed?: boolean | null
  email_verified?: boolean
  onboarding_completed?: boolean
  needs_onboarding?: boolean
  has_google?: boolean
}

class AuthService {
  setToken(token: string, rememberMe = true): void {
    setAuthToken(token, rememberMe)
  }

  getToken(): string | null {
    return getAuthToken()
  }

  removeToken(): void {
    clearAuthToken()
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.getToken()
  }

  // Login user
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      // Long-lived JWT until explicit logout (closing tabs/browser must not sign out).
      const rememberMe = true
      const response = await apiService.post<AuthResponse>('/auth/login', {
        ...credentials,
        remember_me: rememberMe,
      })
      
      console.log('AuthService: Login response received:', response)
      console.log('AuthService: Access token:', response.access_token ? 'Present' : 'Missing')

      if (response.requires_2fa) {
        return response
      }

      if (!response.access_token) {
        throw new Error('Login failed: no access token received.')
      }

      this.setToken(response.access_token, rememberMe)
      console.log('AuthService: Token stored in localStorage')
      
      // Verify token was stored
      const storedToken = this.getToken()
      console.log('AuthService: Stored token verified:', storedToken ? 'Present' : 'Missing')
      
      return response
    } catch (error: any) {
      console.error('Login failed:', error)

      const status = error.response?.status
      const detail = getApiErrorPayload(error.response?.data)
      const detailObj =
        detail && typeof detail === 'object'
          ? (detail as Record<string, unknown>)
          : null

      if (status === 429) {
        const retryAfter =
          detailObj?.retry_after != null
            ? Number(detailObj.retry_after)
            : 300
        const minutes = Math.max(1, Math.ceil(retryAfter / 60))
        throw new Error(
          `Too many login attempts. Please wait about ${minutes} minute(s) and try again.`
        )
      }

      if (status === 400 && detailObj?.requires_captcha) {
        const siteKey =
          typeof detailObj.captcha_site_key === 'string'
            ? detailObj.captcha_site_key
            : undefined
        throw createCaptchaRequiredError(
          'Please complete the CAPTCHA verification and try again.',
          siteKey,
        )
      }

      if (status === 500) {
        const errorDetail =
          typeof detail === 'string' ? detail : 'Internal server error'
        throw new Error(
          `Login failed: ${errorDetail}. Please check if the backend server is running.`
        )
      }

      if (status === 401) {
        throw new Error(
          'Invalid email or password. Please check your credentials and try again.'
        )
      }

      if (status === 400 && typeof detail === 'string') {
        throw new Error(detail)
      }

      if (error.code === 'ERR_NETWORK' || error.message?.includes('ECONNREFUSED')) {
        throw new Error('Cannot connect to server. Please ensure the backend server is running.')
      }

      const friendly = formatApiDetail(detail)
      if (friendly) {
        throw new Error(friendly)
      }

      throw error
    }
  }

  // Register user
  async register(userData: RegisterRequest): Promise<UserResponse> {
    try {
      const response = await apiService.post<UserResponse>('/auth/register', userData)
      return response
    } catch (error: any) {
      console.error('Registration failed:', error)

      if (error.code === 'ERR_NETWORK' || error.message?.includes('ECONNREFUSED')) {
        throw new Error('Cannot connect to server. Please ensure the backend server is running on port 8001.')
      }

      const status = error.response?.status
      const detail = getApiErrorPayload(error.response?.data)
      const friendly = formatApiDetail(detail)
      const captchaInfo = parseCaptchaRequiredFromResponse(error.response?.data)

      if (status === 429) {
        throw new Error(
          friendly || 'Too many registration attempts. Please try again later.',
        )
      }

      if ((status === 400 || status === 422) && captchaInfo.required) {
        throw createCaptchaRequiredError(
          friendly || 'Please complete the CAPTCHA verification and try again.',
          captchaInfo.siteKey,
        )
      }

      if (status === 500) {
        throw new Error(
          `Registration failed: ${friendly || 'Internal server error'}. Please check if the backend server is running.`,
        )
      }

      // 400 business errors and 422 Pydantic validation — surface real messages
      if (status === 400 || status === 422) {
        if (detail && typeof detail === 'object' && typeof (detail as { code?: string }).code === 'string') {
          const code = (detail as { code: string }).code
          const err = new Error(friendly || 'Registration failed') as Error & { code?: string }
          err.code = code
          throw err
        }
        throw new Error(friendly || 'Registration failed: Invalid registration data')
      }

      if (friendly) {
        throw new Error(friendly)
      }

      throw error
    }
  }

  // Get current user info
  async getCurrentUser(): Promise<UserResponse> {
    try {
      const token = this.getToken()
      if (!token) {
        throw new Error('No authentication token')
      }

      console.log('AuthService: Getting current user with token:', token.substring(0, 20) + '...')
      
      const response = await apiService.get<UserResponse>('/auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      console.log('AuthService: getCurrentUser response:', response)
      console.log('AuthService: Response type:', typeof response)
      console.log('AuthService: Response keys:', Object.keys(response))
      console.log('AuthService: Photo URL in response:', response.photo_url)
      console.log('AuthService: Photo field in response:', response.photo)
      
      // Map snake_case fields to camelCase for frontend compatibility
      const mappedResponse = {
        ...response,
        firstName: response.first_name,
        lastName: response.last_name,
        schoolName: response.school_name,
        schoolId: response.school_id,
        photoUrl: response.photo_url,
        photoFilename: response.photo_filename,
        photoSize: response.photo_size,
        photoMimeType: response.photo_mime_type,
        photoUploadedAt: response.photo_uploaded_at,
        lastSeen: response.last_seen
      }
      
      console.log('AuthService: Mapped response:', mappedResponse)
      console.log('AuthService: Mapped photoUrl:', mappedResponse.photoUrl)
      
      return mappedResponse
    } catch (error: any) {
      console.error('AuthService: Failed to get current user:', error)
      console.error('AuthService: Error type:', typeof error)
      console.error('AuthService: Error message:', error.message)
      console.error('AuthService: Error response:', error.response)
      throw error
    }
  }

  async updateCurrentUser(data: {
    first_name?: string
    last_name?: string
    email?: string
    school_name?: string
    preferred_locale?: string
    voice_preference?: string
  }): Promise<UserResponse> {
    const token = this.getToken()
    if (!token) {
      throw new Error('No authentication token')
    }

    const response = await apiService.put<UserResponse>('/auth/me', data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    return {
      ...response,
      firstName: response.first_name,
      lastName: response.last_name,
      schoolName: response.school_name,
      schoolId: response.school_id,
      photoUrl: response.photo_url,
      photoFilename: response.photo_filename,
      photoSize: response.photo_size,
      photoMimeType: response.photo_mime_type,
      photoUploadedAt: response.photo_uploaded_at,
      lastSeen: response.last_seen,
    }
  }

  // Update password
  async updatePassword(passwordData: {
    currentPassword: string;
    newPassword: string;
  }): Promise<{ message: string }> {
    try {
      const token = this.getToken()
      if (!token) {
        throw new Error('No authentication token')
      }

      // Convert camelCase to snake_case for backend
      const backendData = {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }

      const response = await apiService.put<{ message: string }>('/auth/password', backendData)
      
      return response
    } catch (error) {
      console.error('Password update failed:', error)
      throw error
    }
  }

  // Logout user
  async logout(): Promise<void> {
    try {
      const token = this.getToken()
      if (token) {
        await apiService.post('/auth/logout', {})
      }
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      // Always remove token locally
      this.removeToken()
    }
  }

  // Create admin user (admin-only)
  async createAdmin(adminData: {
    firstName: string
    lastName: string
    email: string
    password: string
  }): Promise<UserResponse> {
    try {
      const token = this.getToken()
      if (!token) {
        throw new Error('No authentication token')
      }

      const registerData = {
        email: adminData.email,
        password: adminData.password,
        first_name: adminData.firstName,
        last_name: adminData.lastName,
        role: 'admin' as const,
        school_name: undefined
      }

      const response = await apiService.post<UserResponse>('/users/create-admin', registerData)
      
      return response
    } catch (error) {
      console.error('Admin creation failed:', error)
      throw error
    }
  }

  // Get admin users list
  async getAdminUsers(): Promise<UserResponse[]> {
    try {
      const token = this.getToken()
      if (!token) {
        throw new Error('No authentication token')
      }

      const response = await apiService.get<UserResponse[]>('/users/admins')
      
      return response
    } catch (error) {
      console.error('Failed to get admin users:', error)
      throw error
    }
  }

  // Update user password (admin-only)
  async updateUserPassword(userId: string, passwordData: {
    currentPassword: string
    newPassword: string
    confirmPassword: string
  }): Promise<{ message: string }> {
    try {
      const token = this.getToken()
      if (!token) {
        throw new Error('No authentication token')
      }

      // Convert camelCase to snake_case for backend compatibility
      const backendData = {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
        confirm_password: passwordData.confirmPassword
      }
      
      const response = await apiService.put<{ message: string }>(`/users/${userId}/password`, backendData)
      
      return response
    } catch (error) {
      console.error('Failed to update user password:', error)
      throw error
    }
  }

  async getDetailedUserProfile(userId: string): Promise<any> {
    try {
      const token = this.getToken()
      if (!token) {
        throw new Error('No authentication token')
      }

      const response = await apiService.get<any>(`/users/${userId}/detailed`)
      return response
    } catch (error) {
      console.error('Failed to get detailed user profile:', error)
      throw error
    }
  }

  // Get all users with optional filtering and pagination
  async getAllUsers(params?: {
    page?: number
    limit?: number
    role?: string
    search?: string
  }): Promise<UserResponse[]> {
    try {
      const token = this.getToken()
      if (!token) {
        throw new Error('No authentication token')
      }

      const queryParams = new URLSearchParams()
      if (params?.page) queryParams.append('page', params.page.toString())
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.role) queryParams.append('role', params.role)
      if (params?.search) queryParams.append('search', params.search)

      const url = `/users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      const response = await apiService.get<UserResponse[]>(url)
      
      return response
    } catch (error) {
      console.error('Failed to get all users:', error)
      throw error
    }
  }

  // Refresh token (if needed in the future)
  async refreshToken(): Promise<string> {
    // Implementation for token refresh
    throw new Error('Token refresh not implemented yet')
  }

  // Update user status (admin-only)
  async updateUserStatus(userId: string, isActive: boolean): Promise<{ message: string }> {
    try {
      const token = this.getToken()
      if (!token) {
        throw new Error('No authentication token')
      }

      const response = await apiService.put<{ message: string }>(`/users/${userId}/status`, {
        is_active: isActive
      })
      
      return response
    } catch (error) {
      console.error('Failed to update user status:', error)
      throw error
    }
  }

  // Verify a payout method (admin only)
  async verifyPayoutMethod(payoutMethodId: string, verificationNotes?: string): Promise<any> {
    try {
      const token = this.getToken()
      if (!token) {
        throw new Error('No authentication token')
      }

      const response = await apiService.post(`/payout-methods/${payoutMethodId}/verify`, {
        verification_notes: verificationNotes
      })
      
      return response
    } catch (error) {
      console.error('Failed to verify payout method:', error)
      throw error
    }
  }

  async checkEmailExists(email: string): Promise<{ exists: boolean; role?: string | null }> {
    return apiService.get<{ exists: boolean; role?: string | null }>(
      `/auth/check-email?email=${encodeURIComponent(email)}`,
    )
  }

  async getGoogleAuthConfig(): Promise<{ enabled: boolean; client_id?: string | null }> {
    return apiService.get<{ enabled: boolean; client_id?: string | null }>('/auth/google/config')
  }
}

export const authService = new AuthService()

